import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

interface BoRole {
  id: number;
  name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean;
  assigned_count: number;
}

interface RolesResponse {
  roles: BoRole[];
  available_permissions: string[];
  restricted_permissions: string[];
}

interface AdminRow {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  suspended_at: string | null;
  role_type: string;
  bo_role_id: number | null;
  bo_role_name: string | null;
  /** null means "every permission" — super_admin holds them implicitly. */
  permissions: string[] | null;
}

interface PendingInvite {
  invite_id: number;
  email: string;
  bo_role_name: string | null;
  expires_at: string;
  expired: boolean;
}

interface AdminsResponse {
  admins: AdminRow[];
  pending_invites: PendingInvite[];
}

/** `users.view` -> `Users · view` for the permission matrix labels. */
function labelFor(permission: string): string {
  const [group, action] = permission.split('.');
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return `${cap(group)} · ${action}`;
}

export function TeamPage() {
  const { session } = useAuth();
  const isSuperAdmin = session?.role.role_type === 'super_admin';

  const [data, setData] = useState<AdminsResponse | null>(null);
  const [roleData, setRoleData] = useState<RolesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    bo_role_id: 0,
  });
  const [inviting, setInviting] = useState(false);

  const [editingRole, setEditingRole] = useState<BoRole | null>(null);
  const [draftPermissions, setDraftPermissions] = useState<string[]>([]);
  const [savingRole, setSavingRole] = useState(false);

  const [newRoleName, setNewRoleName] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [admins, roles] = await Promise.all([
        api.get<AdminsResponse>('/back-office/admins'),
        api.get<RolesResponse>('/back-office/bo-roles'),
      ]);
      setData(admins.data);
      setRoleData(roles.data);
      if (roles.data.roles.length > 0) {
        setInviteForm((f) =>
          f.bo_role_id ? f : { ...f, bo_role_id: roles.data.roles[0].id }
        );
      }
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Could not load the admin team'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Wrap a mutation so every path refreshes and surfaces its own error. */
  const run = async (
    fn: () => Promise<unknown>,
    successMessage: string,
    setBusy: (b: boolean) => void
  ) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await fn();
      setNotice(successMessage);
      await load();
      return true;
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Action failed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () => api.post('/back-office/admins/invite', inviteForm),
      `Invitation sent to ${inviteForm.email}`,
      setInviting
    );
    if (ok) {
      setShowInvite(false);
      setInviteForm((f) => ({
        ...f,
        email: '',
        first_name: '',
        last_name: '',
      }));
    }
  };

  const openRoleEditor = (role: BoRole) => {
    setEditingRole(role);
    setDraftPermissions(role.permissions);
  };

  const togglePermission = (permission: string) => {
    setDraftPermissions((current) =>
      current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission]
    );
  };

  const saveRole = async () => {
    if (!editingRole) return;
    const ok = await run(
      () =>
        api.post(`/back-office/bo-roles/${editingRole.id}`, {
          permissions: draftPermissions,
        }),
      `"${editingRole.name}" permissions updated`,
      setSavingRole
    );
    if (ok) setEditingRole(null);
  };

  const createRole = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () =>
        api.post('/back-office/bo-roles', {
          name: newRoleName,
          permissions: [],
        }),
      `Role "${newRoleName}" created`,
      setCreatingRole
    );
    if (ok) setNewRoleName('');
  };

  const changeAdminRole = (userId: number, boRoleId: number) =>
    run(
      () =>
        api.post(`/back-office/admins/${userId}/role`, {
          bo_role_id: boRoleId,
        }),
      'Role updated — it takes effect within 15 minutes, or on their next sign-in',
      () => undefined
    );

  const allPermissions = roleData?.available_permissions ?? [];
  const restricted = roleData?.restricted_permissions ?? [];

  return (
    <div className="px-8 py-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Team &amp; Roles
          </h1>
          <p className="text-sm text-slate-500">
            Invite back-office admins and control what each role can do.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowInvite((s) => !s)}
          className="rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          {showInvite ? 'Cancel' : 'Invite admin'}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          {notice}
        </div>
      )}

      {showInvite && (
        <form
          onSubmit={submitInvite}
          className="mt-4 grid gap-3 rounded border border-slate-200 bg-white p-4 sm:grid-cols-4"
        >
          <div>
            <label className="block text-xs font-medium text-slate-600">
              First name
            </label>
            <input
              required
              value={inviteForm.first_name}
              onChange={(e) =>
                setInviteForm({ ...inviteForm, first_name: e.target.value })
              }
              className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">
              Last name
            </label>
            <input
              required
              value={inviteForm.last_name}
              onChange={(e) =>
                setInviteForm({ ...inviteForm, last_name: e.target.value })
              }
              className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">
              Email
            </label>
            <input
              required
              type="email"
              value={inviteForm.email}
              onChange={(e) =>
                setInviteForm({ ...inviteForm, email: e.target.value })
              }
              className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600">
                Role
              </label>
              <select
                value={inviteForm.bo_role_id}
                onChange={(e) =>
                  setInviteForm({
                    ...inviteForm,
                    bo_role_id: Number(e.target.value),
                  })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {roleData?.roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={inviting}
              className="rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {inviting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      )}

      {/* Admins ------------------------------------------------------------- */}
      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-slate-500">
        Admins
      </h2>
      <div className="mt-2 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && data?.admins.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-slate-500">
                  No admins yet.
                </td>
              </tr>
            )}
            {data?.admins.map((a) => {
              const isSelf = a.user_id === session?.sub;
              const targetIsSuper = a.role_type === 'super_admin';
              const locked = isSelf || (targetIsSuper && !isSuperAdmin);
              return (
                <tr key={`${a.user_id}-${a.role_type}`}>
                  <td className="px-4 py-3">
                    {a.first_name} {a.last_name}
                    {isSelf && (
                      <span className="ml-2 text-xs text-slate-400">(you)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{a.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        targetIsSuper
                          ? 'rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
                          : 'text-xs text-slate-500'
                      }
                    >
                      {a.role_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {targetIsSuper ? (
                      <span className="text-xs text-slate-500">
                        All permissions
                      </span>
                    ) : (
                      <select
                        value={a.bo_role_id ?? ''}
                        disabled={locked}
                        onChange={(e) =>
                          changeAdminRole(a.user_id, Number(e.target.value))
                        }
                        className="rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        <option value="">— none —</option>
                        {roleData?.roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {a.suspended_at ? (
                      <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        Suspended
                      </span>
                    ) : (
                      <span className="text-xs text-emerald-600">Active</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pending invitations ------------------------------------------------ */}
      {(data?.pending_invites.length ?? 0) > 0 && (
        <>
          <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Pending invitations
          </h2>
          <div className="mt-2 overflow-hidden rounded border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.pending_invites.map((inv) => (
                  <tr key={inv.invite_id}>
                    <td className="px-4 py-3 text-slate-600">{inv.email}</td>
                    <td className="px-4 py-3">{inv.bo_role_name ?? '—'}</td>
                    <td className="px-4 py-3">
                      {inv.expired ? (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          Expired
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">
                          {new Date(inv.expires_at).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          run(
                            () =>
                              api.post(
                                `/back-office/admins/invites/${inv.invite_id}/resend`
                              ),
                            `Invitation re-sent to ${inv.email}`,
                            () => undefined
                          )
                        }
                        className="text-brand-600 hover:underline"
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          run(
                            () =>
                              api.delete(
                                `/back-office/admins/invites/${inv.invite_id}`
                              ),
                            'Invitation revoked',
                            () => undefined
                          )
                        }
                        className="ml-4 text-red-600 hover:underline"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Roles -------------------------------------------------------------- */}
      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-slate-500">
        Roles
      </h2>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {roleData?.roles.map((r) => (
          <div
            key={r.id}
            className="rounded border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-slate-800">{r.name}</div>
                <div className="text-xs text-slate-500">
                  {r.assigned_count} admin(s) · {r.permissions.length}{' '}
                  permission(s)
                </div>
              </div>
              {r.is_system && (
                <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-500">
                  System
                </span>
              )}
            </div>
            {r.description && (
              <p className="mt-2 text-xs text-slate-500">{r.description}</p>
            )}
            <div className="mt-3 flex gap-3 text-sm">
              <button
                type="button"
                onClick={() => openRoleEditor(r)}
                className="text-brand-600 hover:underline"
              >
                Edit permissions
              </button>
              {!r.is_system && r.assigned_count === 0 && (
                <button
                  type="button"
                  onClick={() =>
                    run(
                      () => api.delete(`/back-office/bo-roles/${r.id}`),
                      `Role "${r.name}" deleted`,
                      () => undefined
                    )
                  }
                  className="text-red-600 hover:underline"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={createRole} className="mt-4 flex items-end gap-2">
        <div>
          <label className="block text-xs font-medium text-slate-600">
            New role name
          </label>
          <input
            required
            minLength={2}
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            placeholder="e.g. Finance Reviewer"
            className="mt-1 w-64 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <button
          type="submit"
          disabled={creatingRole}
          className="rounded border border-slate-300 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {creatingRole ? 'Creating…' : 'Create role'}
        </button>
      </form>

      {/* Permission matrix -------------------------------------------------- */}
      {editingRole && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-lg bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-800">
              {editingRole.name} permissions
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Changes apply to every admin on this role.
            </p>

            <div className="mt-4 space-y-2">
              {allPermissions.map((p) => {
                const isRestricted = restricted.includes(p);
                const blocked = isRestricted && !isSuperAdmin;
                return (
                  <label
                    key={p}
                    className={`flex items-center gap-3 rounded border px-3 py-2 text-sm ${
                      blocked
                        ? 'border-slate-100 bg-slate-50 text-slate-400'
                        : 'border-slate-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={draftPermissions.includes(p)}
                      disabled={blocked}
                      onChange={() => togglePermission(p)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="flex-1">{labelFor(p)}</span>
                    {isRestricted && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase text-amber-800">
                        Super admin only
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingRole(null)}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRole}
                disabled={savingRole}
                className="rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {savingRole ? 'Saving…' : 'Save permissions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
